import type { Address } from '@ad-lucy/context/adData/storesData/types';
import type { Location, LocationWithCoordinate } from '@ad-lucy/utils/location';

import {
  DEFAULT_RADIUS_IN_KILOMETERS,
  EARTH_RADIUS_IN_KILOMETERS,
  MAX_SEARCH_DISTANCE,
} from './constants';

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export interface StoreCoordinate extends Coordinate {
  address: Address;
  id: string;
  name: string;
}

export interface ProximityCoordinate<T extends Coordinate> {
  distance: number;
  coordinate: T;
}

const radToDeg = (angle: number): number => angle * (180 / Math.PI);
const degToRad = (angle: number): number => angle * (Math.PI / 180);

const isInBoundary = (coordinate: Coordinate, origin: Coordinate, radius: number): boolean => {
  const maxLatitude: number = origin.latitude + radToDeg(radius / EARTH_RADIUS_IN_KILOMETERS);
  const minLatitude: number = origin.latitude - radToDeg(radius / EARTH_RADIUS_IN_KILOMETERS);
  const maxLongitude: number =
    origin.longitude +
    radToDeg(radius / EARTH_RADIUS_IN_KILOMETERS / Math.cos(degToRad(origin.latitude)));

  const minLongitude: number =
    origin.longitude -
    radToDeg(radius / EARTH_RADIUS_IN_KILOMETERS / Math.cos(degToRad(origin.latitude)));

  return (
    coordinate.latitude <= maxLatitude &&
    coordinate.latitude >= minLatitude &&
    coordinate.longitude <= maxLongitude &&
    coordinate.latitude >= minLongitude
  );
};

const getDistanceInKm = (coordinateA: Coordinate, coordinateB: Coordinate): number => {
  const latitudeInRadians: number = degToRad(coordinateA.latitude - coordinateB.latitude);
  const longitudeInRadians: number = degToRad(coordinateA.longitude - coordinateB.longitude);

  const distance: number =
    Math.sin(latitudeInRadians / 2) * Math.sin(latitudeInRadians / 2) +
    Math.cos(degToRad(coordinateB.latitude)) *
      Math.cos(degToRad(coordinateA.latitude)) *
      Math.sin(longitudeInRadians / 2) *
      Math.sin(longitudeInRadians / 2);

  return (
    EARTH_RADIUS_IN_KILOMETERS * (2 * Math.atan2(Math.sqrt(distance), Math.sqrt(1 - distance)))
  );
};

const getCoordinatesInRadiusOfOrigin = <T extends Coordinate>(
  coordinates: T[],
  origin: Coordinate,
  radius: number,
): ProximityCoordinate<T>[] =>
  coordinates
    .reduce((proximityCoordinates: ProximityCoordinate<T>[], coordinate) => {
      if (isInBoundary(coordinate, origin, radius)) {
        const pCoordinate: ProximityCoordinate<T> = {
          coordinate,
          distance: getDistanceInKm(coordinate, origin),
        };

        return proximityCoordinates.concat(pCoordinate);
      }
      return proximityCoordinates;
    }, [])
    .sort((a, b) => {
      if (a.distance > b.distance) return 1;
      if (b.distance > a.distance) return -1;
      return 0;
    });

const filterLocationsWithoutCoordinates = (
  location: Location,
): location is LocationWithCoordinate =>
  typeof location.latitude === 'number' && typeof location.longitude === 'number';

const findNearestCoordinates = <T extends Coordinate>(
  coordinates: T[],
  coordinate: Coordinate,
  radius = DEFAULT_RADIUS_IN_KILOMETERS,
): ProximityCoordinate<T>[] => {
  const coordinatesInBoundary = getCoordinatesInRadiusOfOrigin(coordinates, coordinate, radius);

  if (!coordinatesInBoundary.length && radius <= MAX_SEARCH_DISTANCE) {
    return findNearestCoordinates(coordinates, coordinate, radius * 2);
  }

  return coordinatesInBoundary;
};

const findNearestCoordinate = <T extends Coordinate>(
  coordinates: T[],
  coordinate: Coordinate,
  radius = DEFAULT_RADIUS_IN_KILOMETERS,
): ProximityCoordinate<T> | undefined => findNearestCoordinates(coordinates, coordinate, radius)[0];

export const CoordinatesUtils = {
  filterLocationsWithoutCoordinates,
  findNearestCoordinates,
  findNearestCoordinate,
};
